if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          viewerDots: this.querySelector('[data-gallery-viewer-dots]'),
        };
        this.mobileSlider = this.querySelector('[id^="Slider-Gallery"]');
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileMql = window.matchMedia('(max-width: 749px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('click', this.onViewerClick.bind(this), true);
          this.elements.viewer.addEventListener('slideChanged', this.onViewerSlideChanged.bind(this));
          if (this.elements.viewer.slider) {
            this.viewerResizeObserver = new ResizeObserver(() => this.syncViewerDots());
            this.viewerResizeObserver.observe(this.elements.viewer.slider);
            window.requestAnimationFrame(() => this.syncViewerDots());
          }
        }

        if (this.mobileSlider && this.elements.viewerDots) {
          this.mobileSlider.addEventListener('scroll', debounce(() => this.syncViewerDots(), 50), { passive: true });
          this.mobileMql.addEventListener('change', () => this.syncViewerDots());
          window.requestAnimationFrame(() => this.syncViewerDots());
        }

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onThumbnailSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onViewerSlideChanged() {
        this.syncViewerDots();
      }

      onThumbnailSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.syncViewerDots();
      }

      setActiveMedia(mediaId, prepend, animate = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        const previousRects = animate ? this.captureMediaRects() : null;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (animate && previousRects) this.animateMediaSwap(previousRects, mediaId);

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.syncViewerDots();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      onViewerClick(event) {
        if (!this.shouldSwapViewerMedia()) return;

        const mediaItem = event.target.closest('[data-media-id]');
        const zoomTrigger = event.target.closest('.product__media-toggle');
        const opener = event.target.closest('.product__modal-opener');

        if (zoomTrigger || !mediaItem || !opener || mediaItem.classList.contains('is-active')) return;

        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

        this.setActiveMedia(mediaItem.dataset.mediaId, true, false);
      }

      shouldSwapViewerMedia() {
        const productRoot = this.closest('.product');
        if (!productRoot) return false;

        if (this.mobileMql.matches) {
          return (
            productRoot.classList.contains('product--stacked') ||
            productRoot.classList.contains('product--mobile-columns')
          );
        }

        return productRoot.classList.contains('product--stacked');
      }

      syncViewerDots() {
        if (!this.elements.viewerDots || !this.elements.viewer) return;

        const mobileSlides = this.mobileMql.matches && this.mobileSlider
          ? Array.from(this.mobileSlider.querySelectorAll('[data-media-id]')).filter((slide) => !slide.hidden)
          : [];
        const usesMobileCarousel = mobileSlides.length > 0;
        const totalPages = usesMobileCarousel
          ? mobileSlides.length
          : Math.max(1, this.elements.viewer.totalPages || this.elements.viewer.sliderItemsToShow?.length || 1);
        let currentPage = Math.min(totalPages, Math.max(1, this.elements.viewer.currentPage || 1));

        if (usesMobileCarousel) {
          const currentSlideIndex = mobileSlides.reduce((closestIndex, slide, index) => {
            const closestDistance = Math.abs(mobileSlides[closestIndex].offsetLeft - this.mobileSlider.scrollLeft);
            const slideDistance = Math.abs(slide.offsetLeft - this.mobileSlider.scrollLeft);
            return slideDistance < closestDistance ? index : closestIndex;
          }, 0);
          currentPage = currentSlideIndex + 1;
          mobileSlides.forEach((slide, index) => slide.classList.toggle('is-active', index === currentSlideIndex));
        }

        if (this.elements.viewerDots.childElementCount !== totalPages) {
          this.elements.viewerDots.innerHTML = Array.from({ length: totalPages }, (_value, index) => {
            const pageNumber = index + 1;
            return `
              <button
                type="button"
                class="slider-counter__link slider-counter__link--dots link${pageNumber === currentPage ? ' slider-counter__link--active' : ''}"
                aria-label="Go to media ${pageNumber} of ${totalPages}"
                aria-controls="${this.elements.viewer.slider?.id || this.mobileSlider?.id || ''}"
                data-gallery-dot-index="${pageNumber}"
                ${pageNumber === currentPage ? 'aria-current="true"' : ''}
              >
                <span class="dot"></span>
              </button>
            `;
          }).join('');

          this.elements.viewerDots.querySelectorAll('[data-gallery-dot-index]').forEach((button) => {
            button.addEventListener('click', this.onViewerDotClick.bind(this));
          });
          return;
        }

        this.elements.viewerDots.querySelectorAll('[data-gallery-dot-index]').forEach((button, index) => {
          const isActive = index + 1 === currentPage;
          button.classList.toggle('slider-counter__link--active', isActive);
          if (isActive) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });
      }

      onViewerDotClick(event) {
        event.preventDefault();
        const pageNumber = Number(event.currentTarget.dataset.galleryDotIndex);
        if (!pageNumber) return;

        if (this.mobileMql.matches && this.mobileSlider) {
          const mobileSlides = Array.from(this.mobileSlider.querySelectorAll('[data-media-id]')).filter(
            (slide) => !slide.hidden
          );
          const targetSlide = mobileSlides[pageNumber - 1];
          if (!targetSlide) return;
          this.mobileSlider.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
          return;
        }

        if (!this.elements.viewer?.sliderItemsToShow?.length) return;
        const targetSlide = this.elements.viewer.sliderItemsToShow[pageNumber - 1];
        if (!targetSlide || !this.elements.viewer.slider) return;
        this.elements.viewer.slider.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
      }

      captureMediaRects() {
        const rects = new Map();
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
          rects.set(item.dataset.mediaId, item.getBoundingClientRect());
        });
        return rects;
      }

      animateMediaSwap(previousRects, activeMediaId) {
        if (!this.mobileMql.matches) {
          const activeItem = this.elements.viewer.querySelector(`[data-media-id="${activeMediaId}"]`);
          if (!activeItem) return;

          activeItem.getAnimations().forEach((animation) => animation.cancel());
          activeItem.animate(
            [
              {
                opacity: 0.9,
              },
              {
                opacity: 1,
              },
            ],
            {
              duration: 180,
              easing: 'ease-out',
            }
          );
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
          const previousRect = previousRects.get(item.dataset.mediaId);
          if (!previousRect) return;

          const currentRect = item.getBoundingClientRect();
          const deltaX = previousRect.left - currentRect.left;
          const deltaY = previousRect.top - currentRect.top;
          const distance = Math.abs(deltaX) + Math.abs(deltaY);

          item.getAnimations().forEach((animation) => animation.cancel());

          if (distance > 1) {
            item.animate(
              [
                {
                  transform: `translate(${deltaX}px, ${deltaY}px) scale(0.996)`,
                  opacity: item.dataset.mediaId === activeMediaId ? 0.9 : 0.86,
                },
                {
                  transform: 'translate(0, 0) scale(1)',
                  opacity: 1,
                },
              ],
              {
                duration: 220,
                easing: 'ease-out',
              }
            );
          } else if (item.dataset.mediaId === activeMediaId) {
            item.animate(
              [
                {
                  opacity: 0.9,
                  transform: 'scale(0.996)',
                },
                {
                  opacity: 1,
                  transform: 'scale(1)',
                },
              ],
              {
                duration: 180,
                easing: 'ease-out',
              }
            );
          }
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
